import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { trackEvent } from '../services/analytics';
import { Button, Badge, Modal } from './index';
import { CompleteSearchModal } from './CompleteSearchModal';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { FlatSearch, PropertySummary } from '../types/api';

interface InviteSectionProps {
  search: FlatSearch;
  properties: PropertySummary[];
  onSearchUpdate: (search: FlatSearch) => void;
}

export function InviteSection({ search, properties, onSearchUpdate }: InviteSectionProps) {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(search.inviteCode ?? null);
  const [loading, setLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generateError, setGenerateError] = useState(false);
  const [membersExpanded, setMembersExpanded] = useState(false);

  const generateInvite = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setGenerateError(false);
    try {
      const result = await api.createInvite(search.id);
      setInviteCode(result.inviteCode);
      trackEvent('partner_invited', { search_id: search.id });
      return true;
    } catch {
      setGenerateError(true);
      return false;
    } finally {
      setLoading(false);
    }
  }, [search.id]);

  const inviteUrl = inviteCode
    ? `${import.meta.env.VITE_WEB_URL}?invite=${inviteCode}`
    : '';

  const copyLink = useCallback(async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available — ignore
    }
  }, [inviteUrl]);

  const shareWhatsApp = useCallback(() => {
    if (!inviteUrl) return;
    const text = t('app.inviteSection.whatsappMessage', { url: inviteUrl });
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  }, [inviteUrl, t]);

  const shareEmail = useCallback(() => {
    if (!inviteUrl) return;
    const subject = t('app.inviteSection.emailSubject');
    const body = t('app.inviteSection.emailBody', { url: inviteUrl });
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  }, [inviteUrl, t]);

  const handleInviteClick = useCallback(async () => {
    if (!inviteCode) {
      const ok = await generateInvite();
      if (!ok) return;
    }
    setShowModal(true);
  }, [inviteCode, generateInvite]);

  const handleArchive = useCallback(async () => {
    setArchiving(true);
    try {
      const updated = await api.updateSearch(search.id, { status: 'archived' });
      onSearchUpdate(updated);
    } catch {
      // Archive failed — ignore
    } finally {
      setArchiving(false);
    }
  }, [search.id, onSearchUpdate]);

  const isSolo = search.members.length === 1;
  const isActive = search.status === 'active';
  const isCompleted = search.status === 'completed';
  const isArchived = search.status === 'archived';

  return (
    <div className="invite-section">
      <div className="invite-top-row">
        <button
          className="invite-members-header"
          onClick={() => setMembersExpanded((v) => !v)}
          aria-expanded={membersExpanded}
        >
          <span className="invite-members-title">{t('app.inviteSection.membersTitle')}</span>
          <Badge variant={isSolo ? 'warning' : 'success'}>
            {search.members.length}
          </Badge>
          <span className="invite-members-chevron">
            {membersExpanded ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
          </span>
        </button>
        {isActive && (
          <div className="invite-actions">
            <Button
              variant={isSolo ? 'primary' : 'secondary'}
              size="sm"
              onClick={handleInviteClick}
              disabled={loading}
            >
              {loading ? t('app.inviteSection.generating') : generateError ? t('app.inviteSection.failedRetry') : t('app.inviteSection.invitePartner')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowCompleteModal(true)}
            >
              {t('app.inviteSection.completeSearch')}
            </Button>
          </div>
        )}
      </div>
      {membersExpanded && (
        <div className="invite-members-list">
          {search.members.map((member) => (
            <div key={member.userId} className="invite-member">
              <span className="invite-member-name">{member.name}</span>
              <span className="invite-member-date">
                {new Date(member.joinedAt).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      )}

      {isCompleted && (
        <div className="search-lifecycle-banner" style={{ marginTop: '8px', padding: '12px', background: 'var(--success-bg, #ecfdf5)', borderRadius: '8px', border: '1px solid var(--success-border, #a7f3d0)' }}>
          <p style={{ fontSize: '14px', fontWeight: 500, marginBottom: '8px' }}>{t('app.inviteSection.searchCompleted')}</p>
          {search.endDate && (
            <p style={{ fontSize: '12px', color: '#666' }}>
              {t('app.inviteSection.endedDate', { date: new Date(search.endDate).toLocaleDateString() })}
            </p>
          )}
          <Button
            variant="secondary"
            size="sm"
            onClick={handleArchive}
            disabled={archiving}
            style={{ marginTop: '8px' }}
          >
            {archiving ? t('app.inviteSection.archiving') : t('app.inviteSection.archive')}
          </Button>
        </div>
      )}

      {isArchived && (
        <div className="search-lifecycle-banner" style={{ marginTop: '8px', padding: '12px', background: '#f3f4f6', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <p style={{ fontSize: '14px', fontWeight: 500, color: '#6b7280' }}>{t('app.inviteSection.searchArchived')}</p>
          {search.endDate && (
            <p style={{ fontSize: '12px', color: '#9ca3af' }}>
              {t('app.inviteSection.endedDate', { date: new Date(search.endDate).toLocaleDateString() })}
            </p>
          )}
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={t('app.inviteSection.inviteModalTitle')}>
        {inviteCode ? (
          <div className="invite-modal-content">
            <p className="invite-modal-desc">{t('app.inviteSection.inviteDescription')}</p>
            <div className="invite-code-display">
              <code>{inviteUrl}</code>
            </div>
            <div className="invite-share-buttons">
              <Button onClick={copyLink} variant="primary" size="sm">
                {copied ? t('common.copied') : t('app.inviteSection.copyLink')}
              </Button>
              <Button onClick={shareWhatsApp} variant="secondary" size="sm">
                {t('app.inviteSection.whatsapp')}
              </Button>
              <Button onClick={shareEmail} variant="secondary" size="sm">
                {t('app.inviteSection.email')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="invite-modal-content">
            <p>{t('app.inviteSection.generatingLink')}</p>
          </div>
        )}
      </Modal>

      <CompleteSearchModal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        searchId={search.id}
        properties={properties}
        onComplete={onSearchUpdate}
      />
    </div>
  );
}
